import React, { useState } from 'react';
import './OrderCard.css';

const OrderCard = ({ order, onStatusChange, onViewDetails }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Calculate time ago
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  // Get next status
  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'in_progress': 'prepared',
      'prepared': 'out_for_delivery',
      'out_for_delivery': 'delivered'
    };
    return statusFlow[currentStatus];
  };

  // Get status button text
  const getStatusButtonText = (currentStatus) => {
    const buttonText = {
      'in_progress': 'Mark as Prepared',
      'prepared': 'Out for Delivery',
      'out_for_delivery': 'Mark as Delivered'
    };
    return buttonText[currentStatus] || 'Update Status';
  };

  const handleStatusChange = async () => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return;

    setIsUpdating(true);
    await onStatusChange(order.id, nextStatus);
    setIsUpdating(false);
  };

  const totalItems = order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  // Get order type badge
  const getOrderTypeBadge = () => {
    if (!order.orderType) return null;

    const orderTypeConfig = {
      delivery: { icon: '🚚', text: 'Delivery', className: 'delivery' },
      takeaway: { icon: '🏃', text: 'Takeaway', className: 'takeaway' },
      pickup: { icon: '🏃', text: 'Pickup', className: 'pickup' },
      'dine-in': { icon: '🍽️', text: 'Dine-In', className: 'dine-in' },
      dinein: { icon: '🍽️', text: 'Dine-In', className: 'dine-in' }
    };

    const config = orderTypeConfig[order.orderType.toLowerCase()];
    if (!config) return null;

    return (
      <div className={`order-type-badge ${config.className}`}>
        <span className="badge-icon">{config.icon}</span>
        <span className="badge-text">{config.text}</span>
      </div>
    );
  };

  // Get order source badge (phone, online, web, etc.)
  const getOrderSourceBadge = () => {
    const source = order.orderSource || order.source || 'web';

    const sourceConfig = {
      phone: { icon: '📞', text: 'Phone', className: 'source-phone' },
      online: { icon: '🌐', text: 'Online', className: 'source-online' },
      web: { icon: '💻', text: 'Web', className: 'source-web' },
      app: { icon: '📱', text: 'App', className: 'source-app' },
      whatsapp: { icon: '💬', text: 'WhatsApp', className: 'source-whatsapp' }
    };

    const config = sourceConfig[source.toLowerCase()] || sourceConfig['web'];

    return (
      <div className={`order-source-badge ${config.className}`}>
        <span className="badge-icon">{config.icon}</span>
        <span className="badge-text">{config.text}</span>
      </div>
    );
  };

  return (
    <div className={`order-card order-card-${order.status}`}>
      <div className="order-card-header">
        <div className="order-header-top">
          <div className="order-id">
            Order #{order.orderNumber || order.id.slice(-4)}
          </div>
          <div className="order-badges">
            {getOrderTypeBadge()}
            {getOrderSourceBadge()}
          </div>
        </div>
        <div className="order-time">{formatTime(order.timestamp)}</div>
      </div>

      {order.outletName && (
        <div className="order-outlet">
          🏪 <strong>{order.outletName}</strong>
        </div>
      )}

      <div className="order-time-ago">
        ⏱️ {getTimeAgo(order.timestamp)}
      </div>

      <div className="order-customer">
        <div className="customer-name">
          👤 {order.customer?.name || 'N/A'}
        </div>
        <div className="customer-phone">
          📱 {order.customer?.phone || 'N/A'}
        </div>
        <div className="customer-location">
          📍 {order.customer?.location || 'N/A'}
        </div>
      </div>

      <div className="order-items">
        <div className="items-header">Items ({totalItems}):</div>
        <div className="items-list">
          {order.items?.slice(0, 2).map((item, idx) => (
            <div key={idx} className="item-row">
              <span className="veg-icon" style={{
                borderColor: item.isVeg ? '#4CAF50' : '#f44336',
                display: 'inline-flex'
              }}>
                <span className="veg-dot" style={{
                  backgroundColor: item.isVeg ? '#4CAF50' : '#f44336'
                }}></span>
              </span>
              <span className="item-text">
                {item.quantity}x {item.name}
              </span>
              <span className="item-price">₹{item.total || item.price * item.quantity}</span>
            </div>
          ))}
          {order.items?.length > 2 && (
            <div className="more-items">+{order.items.length - 2} more items</div>
          )}
        </div>
      </div>

      <div className="order-total">
        <span>💰 Total:</span>
        <span className="total-amount">₹{order.pricing?.total || 0}</span>
      </div>

      <div className="order-payment">
        <span>💳 Payment:</span>
        <span className="payment-method">
          {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Paid'}
        </span>
      </div>

      {order.specialInstructions && (
        <div className="order-notes">
          📝 {order.specialInstructions}
        </div>
      )}

      <div className="order-actions">
        <button
          className="btn-view-details"
          onClick={() => onViewDetails(order)}
        >
          View Full Details
        </button>

        {order.status !== 'delivered' && order.status !== 'cancelled' && (
          <button
            className="btn-update-status"
            onClick={handleStatusChange}
            disabled={isUpdating}
          >
            {isUpdating ? 'Updating...' : getStatusButtonText(order.status)} →
          </button>
        )}
      </div>
    </div>
  );
};

export default OrderCard;
